import {
  CognitoUser,
  CognitoUserAttribute,
  CognitoUserPool,
  AuthenticationDetails,
  CognitoUserSession,
} from "amazon-cognito-identity-js";

/**
 * Local dev (VITE_AUTH_MODE=local): no Cognito at all. The backend's local
 * sign-in bypass supplies the user, so these helpers become no-ops.
 */
export const LOCAL_AUTH = import.meta.env.VITE_AUTH_MODE === "local";

const userPoolId = import.meta.env.VITE_USER_POOL_ID as string | undefined;
const clientId = import.meta.env.VITE_USER_POOL_CLIENT_ID as string | undefined;

let pool: CognitoUserPool | null = null;

/** Created lazily: the SDK throws at construction if the IDs are missing. */
function userPool(): CognitoUserPool {
  if (!pool) {
    if (!userPoolId || !clientId) {
      throw new Error("Cognito not configured: set VITE_USER_POOL_ID and VITE_USER_POOL_CLIENT_ID in .env");
    }
    pool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });
  }
  return pool;
}

export interface SignInResult {
  session: CognitoUserSession;
  user: CognitoUser;
}

export function getCurrentUser(): CognitoUser | null {
  if (LOCAL_AUTH) return null;
  return userPool().getCurrentUser();
}

// Two-step sign-in (2026-10-27): the person waiting for their authenticator code.
let pendingMfa: CognitoUser | null = null;

export function signIn(email: string, password: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool() });
    const auth = new AuthenticationDetails({ Username: email, Password: password });

    user.authenticateUser(auth, {
      onSuccess: (session) => resolve({ session, user }),
      onFailure: (err) => reject(err),
      newPasswordRequired: () => {
        const err = new Error("NEW_PASSWORD_REQUIRED");
        (err as Error & { code?: string }).code = "NEW_PASSWORD_REQUIRED";
        reject(err);
      },
      totpRequired: () => {
        pendingMfa = user;
        const err = new Error("MFA_CODE_REQUIRED");
        (err as Error & { code?: string }).code = "MFA_CODE_REQUIRED";
        reject(err);
      },
    });
  });
}

/** Finish a password sign-in with the 6-digit authenticator code. */
export function submitMfaCode(code: string): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const user = pendingMfa;
    if (!user) return reject(new Error("Start again: enter your email and password."));
    user.sendMFACode(code, {
      onSuccess: (session) => { pendingMfa = null; resolve({ session, user }); },
      onFailure: (err) => reject(err),
    }, "SOFTWARE_TOKEN_MFA");
  });
}

/** Set up an authenticator for the signed-in person: returns the secret to show as a QR code. */
export function startTotpSetup(): Promise<string> {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) return reject(new Error("Not signed in."));
    user.getSession((err: Error | null) => {
      if (err) return reject(err);
      user.associateSoftwareToken({ associateSecretCode: (secret: string) => resolve(secret), onFailure: (e: Error) => reject(e) });
    });
  });
}

/** Check the first code from the app, then make the authenticator the sign-in step. */
export function finishTotpSetup(code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) return reject(new Error("Not signed in."));
    user.getSession((err: Error | null) => {
      if (err) return reject(err);
      user.verifySoftwareToken(code, "DocVault", {
        onSuccess: () => user.setUserMfaPreference(null, { PreferredMfa: true, Enabled: true }, (e) => (e ? reject(e) : resolve())),
        onFailure: (e: Error) => reject(e),
      });
    });
  });
}

export function completeNewPassword(
  email: string,
  oldPassword: string,
  newPassword: string,
  attributes: Record<string, string> = {}
): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool() });
    const auth = new AuthenticationDetails({ Username: email, Password: oldPassword });

    user.authenticateUser(auth, {
      onSuccess: (session) => resolve({ session, user }),
      onFailure: (err) => reject(err),
      newPasswordRequired: () => {
        user.completeNewPasswordChallenge(newPassword, attributes, {
          onSuccess: (session) => resolve({ session, user }),
          onFailure: (err) => reject(err),
        });
      },
    });
  });
}

/** Email a reset code (Cognito "forgot password"). */
export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool() });
    user.forgotPassword({ onSuccess: () => resolve(), onFailure: (err) => reject(err), inputVerificationCode: () => resolve() });
  });
}

/** Finish a reset with the emailed code. */
export function confirmNewPassword(email: string, code: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: userPool() });
    user.confirmPassword(code, newPassword, { onSuccess: () => resolve(), onFailure: (err) => reject(err) });
  });
}

/** Change the signed-in user's password. */
export function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) return reject(new Error("Not signed in."));
    user.getSession((err: Error | null) => {
      if (err) return reject(err);
      user.changePassword(oldPassword, newPassword, (e) => (e ? reject(e) : resolve()));
    });
  });
}

/** Plain-English message for a Cognito error. */
export function cognitoMessage(err: unknown): string {
  const e = err as { code?: string; name?: string; message?: string };
  const code = e?.code || e?.name || "";
  if (code === "NotAuthorizedException" && /session/i.test(e.message ?? "")) return "That took too long. Sign in again.";
  if (code === "NotAuthorizedException") return "That email and password don't match.";
  if (code === "UserNotFoundException") return "That email and password don't match.";
  if (code === "PasswordResetRequiredException") return "You need to reset your password. Use Forgot password.";
  if (code === "CodeMismatchException") return "That code isn't right. Check it and try again.";
  if (code === "EnableSoftwareTokenMFAException") return "That code isn't right. Wait for the next code in your app and try again.";
  if (code === "ExpiredCodeException" && /session/i.test(e.message ?? "")) return "That took too long. Sign in again.";
  if (code === "ExpiredCodeException") return "That code has expired. Request a new one.";
  if (code === "InvalidPasswordException") return e.message?.replace(/^Password does not conform to policy: /, "") || "That password doesn't meet the rules.";
  if (code === "LimitExceededException" || code === "TooManyRequestsException") return "Too many attempts. Wait a few minutes and try again.";
  return e?.message || "Something went wrong. Try again.";
}

export function signOut(): void {
  const u = getCurrentUser();
  if (u) u.signOut();
}

export async function getIdToken(): Promise<string | null> {
  if (LOCAL_AUTH) return null;
  const user = getCurrentUser();
  if (!user) return null;

  return new Promise((resolve) => {
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session?.isValid()) return resolve(null);
      resolve(session.getIdToken().getJwtToken());
    });
  });
}

// Re-export so other modules don't import the SDK directly.
export type { CognitoUser, CognitoUserSession, CognitoUserAttribute };
